import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { importSteps } from '../assets/import_steps_icons';
import { UserGroupIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import Footer from './Footer';

const ImportsPage: React.FC = () => {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Professional stepper layout
  const steps = importSteps.map((step, idx) => {
    const Icon = step.icon;
    // Map step index to translation keys
    const stepKeyMap = [
      {
        title: t('importSteps.registerTitle'),
        desc: t('importSteps.registerDesc'),
        link: step.link,
      },
      {
        title: t('importSteps.quotationTitle'),
        desc: t('importSteps.quotationDesc'),
      },
      {
        title: t('importSteps.approveTitle'),
        desc: t('importSteps.approveDesc'),
      },
      {
        title: t('importSteps.shippingTitle'),
        desc: t('importSteps.shippingDesc'),
      },
      {
        title: t('importSteps.receiveTitle'),
        desc: t('importSteps.receiveDesc'),
      },
    ];
    const stepData = stepKeyMap[idx];
    return (
      <div key={idx} className="relative flex flex-col items-center text-center w-40 min-w-[140px] mx-auto md:mx-0">
        <div className="flex items-center justify-center w-14 h-14 rounded-full bg-white dark:bg-gray-900 shadow-lg border-4 border-accent mb-2 z-10">
          <Icon className="w-7 h-7 text-accent" />
        </div>
        <div className="absolute top-7 left-full w-8 h-1 bg-accent/40 hidden md:block" style={{display: idx === importSteps.length-1 ? 'none' : undefined}}></div>
        <div className="flex flex-col items-center bg-white/90 dark:bg-gray-900/90 rounded-xl px-3 py-2 mt-2 shadow-md border border-gray-200 dark:border-gray-800 min-h-[110px]">
          <span className="text-xs font-bold text-accent mb-1">{t('importSteps.step', { number: idx + 1 })}</span>
          <h3 className="font-semibold text-base md:text-lg mb-1 text-gray-900 dark:text-white">{stepData.title}</h3>
          <p className="text-gray-700 dark:text-gray-300 text-xs md:text-sm mb-1">{stepData.desc}</p>
          {stepData.link && (
            <a href={stepData.link} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 underline text-xs md:text-sm">{t('importSteps.goToAfip')}</a>
          )}
        </div>
      </div>
    );
  });

  // English and Spanish content now unified via t()
  const content = (
    <>
      <h1 className="text-3xl font-bold mb-6 text-accent">{t('importingQuestions')}</h1>
      <div className="flex flex-col items-center mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-14 h-14 bg-accent/20 rounded-full flex items-center justify-center">
            <UserGroupIcon className="h-8 w-8 text-accent" />
          </div>
          <div className="text-left">
            <div className="text-lg font-semibold text-accent">Eduardo González</div>
            <div className="text-sm text-gray-600 dark:text-gray-300">{t('team.importSpecialist')}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a href="mailto:eduardo.gonzalez@grupolet.com.ar" className="flex items-center text-blue-600 dark:text-blue-400 underline hover:text-accent transition">
            <EnvelopeIcon className="h-5 w-5 mr-1" />
            <span className="text-sm">eduardo.gonzalez@grupolet.com.ar</span>
          </a>
        </div>
      </div>
      <h2 className="text-2xl font-semibold mt-8 mb-4">{t('importsPage.quotationExample')}</h2>
      <table className="min-w-max w-full text-xs md:text-sm border border-gray-300 dark:border-gray-700 bg-white/90 dark:bg-gray-900/90 rounded-lg mb-8">
        <thead className="bg-gray-100 dark:bg-gray-800">
          <tr>
            <th className="p-2">{t('importsPage.item')}</th>
            <th className="p-2">{t('importsPage.amount')}</th>
          </tr>
        </thead>
        <tbody>
          <tr><td className="p-2">{t('importsPage.fobTotal')}</td><td className="p-2">300000,00</td></tr>
          <tr><td className="p-2">{t('importsPage.freightTotal')}</td><td className="p-2">20000,00</td></tr>
          <tr><td className="p-2">{t('importsPage.insuranceTotal')}</td><td className="p-2">3200,00</td></tr>
          <tr><td className="p-2">{t('importsPage.internalFreight')}</td><td className="p-2">0,00</td></tr>
          <tr className="font-bold bg-gray-50 dark:bg-gray-900"><td className="p-2">{t('importsPage.customsValue')}</td><td className="p-2">323200,00</td></tr>
        </tbody>
      </table>
      <h2 className="text-2xl font-semibold mt-8 mb-4">{t('importsPage.comparison')}</h2>
      
      {/* Savings Highlight Banner */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl p-6 mb-6 text-center shadow-lg">
        <div className="text-2xl md:text-3xl font-extrabold mb-2">{t('importsPage.savingsAmount')}</div>
        <div className="text-lg opacity-90">{t('importsPage.savingsVsText')}</div>
        <div className="text-sm opacity-80 mt-1">{t('importsPage.savingsPercentText')}</div>
      </div>
      
      <div className="overflow-x-auto mb-8">
        <table className="min-w-max w-full text-xs md:text-sm border border-gray-300 dark:border-gray-700 bg-white/90 dark:bg-gray-900/90 rounded-lg">
          <thead className="bg-gray-100 dark:bg-gray-800">
            <tr>
              <th className="p-2"></th>
              <th className="p-2 text-center" colSpan={3}>{t('importsPage.capitalGood')}</th>
              <th className="p-2 text-center" colSpan={3}>{t('importsPage.commercialization')}</th>
            </tr>
            <tr>
              <th className="p-2"></th>
              <th className="p-2">{t('importsPage.percent')}</th>
              <th className="p-2">{t('importsPage.currency')}</th>
              <th className="p-2">{t('importsPage.value')}</th>
              <th className="p-2">{t('importsPage.percent')}</th>
              <th className="p-2">{t('importsPage.currency')}</th>
              <th className="p-2">{t('importsPage.value')}</th>
            </tr>
          </thead>
          <tbody>
            <tr><td className="p-2">{t('importsPage.duties')}</td><td className="p-2">12,60%</td><td className="p-2">USD</td><td className="p-2">40.723,20</td><td className="p-2">12,60%</td><td className="p-2">USD</td><td className="p-2">40.723,20</td></tr>
            <tr><td className="p-2">{t('importsPage.statisticalFee')}</td><td className="p-2">0,00%</td><td className="p-2">USD</td><td className="p-2">-</td><td className="p-2">0,00%</td><td className="p-2">USD</td><td className="p-2">-</td></tr>
            <tr><td className="p-2">{t('importsPage.vatBase')}</td><td className="p-2"></td><td className="p-2">USD</td><td className="p-2">363.923,20</td><td className="p-2"></td><td className="p-2">USD</td><td className="p-2">363.923,20</td></tr>
            <tr><td className="p-2">{t('importsPage.vat')}</td><td className="p-2">10,50%</td><td className="p-2">USD</td><td className="p-2">38.211,94</td><td className="p-2">10,50%</td><td className="p-2">USD</td><td className="p-2">38.211,94</td></tr>
            <tr className="bg-red-50 dark:bg-red-900/20"><td className="p-2">{t('importsPage.additionalVAT')}</td><td className="p-2 text-green-600 font-bold">0,00%</td><td className="p-2">USD</td><td className="p-2 text-green-600 font-bold">-</td><td className="p-2 text-red-600 font-bold">10,00%</td><td className="p-2">USD</td><td className="p-2 text-red-600 font-bold">36.392,32</td></tr>
            <tr className="bg-red-50 dark:bg-red-900/20"><td className="p-2">{t('importsPage.incomeTax')}</td><td className="p-2 text-green-600 font-bold">0,00%</td><td className="p-2">USD</td><td className="p-2 text-green-600 font-bold">-</td><td className="p-2 text-red-600 font-bold">6,00%</td><td className="p-2">USD</td><td className="p-2 text-red-600 font-bold">21.835,39</td></tr>
            <tr className="bg-red-50 dark:bg-red-900/20"><td className="p-2">{t('importsPage.grossIncome')}</td><td className="p-2 text-green-600 font-bold">0,00%</td><td className="p-2">USD</td><td className="p-2 text-green-600 font-bold">-</td><td className="p-2 text-red-600 font-bold">3,00%</td><td className="p-2">USD</td><td className="p-2 text-red-600 font-bold">10.917,70</td></tr>
            <tr className="font-bold bg-gray-50 dark:bg-gray-900">
              <td className="p-2 text-right" colSpan={3}>{t('importsPage.total')}</td>
              <td className="p-2 text-2xl text-green-600">78.935,14</td>
              <td className="p-2 text-right" colSpan={2}>{t('importsPage.total')}</td>
              <td className="p-2 text-2xl text-red-600">148.080,54</td>
            </tr>
            <tr className="bg-green-100 dark:bg-green-900/30 font-bold text-lg">
              <td className="p-3 text-right" colSpan={6} rowSpan={1}>
                <div className="text-green-700 dark:text-green-300">💰 <strong>{t('importsPage.savingsLabel')}</strong> $69,145.40 USD (47%)</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark relative overflow-hidden py-16 px-4 flex flex-col items-center transition-colors duration-300 watermark-bg">
      <div className="max-w-3xl w-full bg-white/80 dark:bg-black/60 rounded-2xl shadow-2xl p-8 border border-gray-200 dark:border-gray-800 z-10">
        <h1 className="text-4xl font-extrabold mb-3 text-accent text-center tracking-tight">{t('importsPage.importMachinery')}</h1>
        <p className="text-lg md:text-xl text-center mb-10 text-gray-700 dark:text-gray-300 font-medium">{t('importsPage.importMachineryDescription')}</p>
        <div className="w-full flex flex-col md:flex-row justify-center items-start md:items-stretch gap-8 md:gap-x-2 mb-12 relative px-2 md:px-6">
          {steps}
        </div>
        <hr className="my-8 border-accent/30" />
        {/* Existing content (tables, etc.) below */}
        {content}
      </div>
    </div>
  );
};

const ImportsPageWithFooter: React.FC = () => (
  <div className="pt-24 md:pt-28">
    <ImportsPage />
    <Footer />
  </div>
);

export default ImportsPageWithFooter;
